import { Text, Box, Flex, Portal, FormErrorMessage, Image } from '@chakra-ui/core'
import { StyledFormControl, StyledFormLabel } from 'components/AdminConsole/commonUI/styles'
import useLocaleText from 'components/useLocaleText'
import { Video } from 'lucide-react'
import React, { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { InputWithIcon } from 'components/AdminConsole/commonUI/InputWithIcon'
import { FileUploadButton } from 'components/TasksV2/TaskCreation/Steps/SubtaskCreationStep/Widgets/Link/FIleUploadButton'
import { useMediaState } from 'hooks/useMediaState'
import { useDropzoneHandlers } from 'hooks/useDropzoneHandlers'
import strings from 'constants/strings'
import { DropzoneWrapper } from 'components/DropzoneWrapper'
import HiddenFileInput from 'components/ui/Input/HiddenFileInput'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { validateVideoUrl } from '../utils'
import FsLightbox from 'fslightbox-react'
import { RemoveButton } from 'components/ui/Button/RemoveButton'
import { isVideo } from 'components/CommonComponents/MediaViewer/utils'
import imagePlaceholderAnimated from 'assets/placeholder-640.svg'

export const UploadVideoField = () => {
  const { register, setValue, triggerValidation, errors } = useFormContext()
  const t = useLocaleText('pop_video', true)
  const t_placeholder = useLocaleText('pop_video_placeholder', true)
  const isRtl = useSelector(isRtlSelector)
  const [isOpenLightBox, setIsOpenLightBox] = useState(false)
  const {
    handleMediaUploading,
    imageInputRef,
    uploadedVideosURL,
    isUploadLoading,
    setUploadedVideosURL,
    media,
    handleDeleteMedia,
  } = useMediaState({
    videos: [],
  })

  const error = errors.data?.video

  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const data = await handleMediaUploading(event)
    if (!data || (!data.uploadedImages.length && !data.uploadedVideos.length)) {
      return
    }

    if (data.uploadedVideos.length) {
      const newVideo = data.uploadedVideos[0]
      setValue('data.video', newVideo)
      triggerValidation?.('data.video')
    }
  }

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const url = event.target.value
    setValue('data.video', url)

    const validationResult = validateVideoUrl(url)
    if (validationResult === true && url) {
      setUploadedVideosURL([url])
    } else if (!url) {
      setUploadedVideosURL([])
    }

    triggerValidation?.('data.video')
  }

  const handleUploadMedia = async (files: File[]) => {
    if (!files.length) return

    const mockEvent = {
      target: {
        files: files as unknown as FileList,
      },
    } as React.ChangeEvent<HTMLInputElement>

    await handleVideoUpload(mockEvent)
  }

  const handleUploadMediaClick = () => {
    imageInputRef.current?.click()
  }

  const openViewer = () => {
    setIsOpenLightBox((prev) => !prev)
  }

  const handleRemoveMedia = () => {
    handleDeleteMedia(0)
    setValue('data.video', '')
    setUploadedVideosURL([])
    triggerValidation?.('data.video')
  }

  // Dropzone handlers
  const { onDrop, handlePaste } = useDropzoneHandlers({
    onUploadMedia: handleUploadMedia,
    accept: `${strings.POST_FILE_ACCEPT_EXTENSIONS},video/*`,
  })

  return (
    <DropzoneWrapper onDrop={onDrop} onPaste={handlePaste}>
      <Flex
        alignItems="flex-end"
        style={{ columnGap: '10px' }}
        opacity={isUploadLoading ? 0.4 : 1}
        pointerEvents={isUploadLoading ? 'none' : 'auto'}
      >
        <StyledFormControl w="100%" isInvalid={!!error}>
          <StyledFormLabel>
            <Video size={18} strokeWidth={2.5} />
            <Text>{t}</Text>
          </StyledFormLabel>
          <Box pos="relative">
            <InputWithIcon
              name="data.video"
              ref={register({
                validate: validateVideoUrl,
              })}
              onChange={handleUrlChange}
              borderRadius="5px"
              pr={isRtl ? '1rem' : '2.5rem'}
              pl={isRtl ? '2.5rem' : '1rem'}
              border="1px solid"
              placeholder={t_placeholder}
              _focus={{ boxShadow: '0px 0px 0px 1px black' }}
            />
            <Box
              pos="absolute"
              right={isRtl ? 'unset' : '10px'}
              left={isRtl ? '10px' : 'unset'}
              top="50%"
              transform="translateY(-50%)"
            >
              <FileUploadButton onClick={handleUploadMediaClick} />
            </Box>
          </Box>

          <FormErrorMessage>{error?.message}</FormErrorMessage>
        </StyledFormControl>
        {(uploadedVideosURL[0] || isUploadLoading) && (
          <Box position="relative" width="50px" height="50px" borderRadius="10px" overflow="hidden" cursor="pointer">
            {isUploadLoading ? (
              <Image src={imagePlaceholderAnimated} width="100%" height="100%" alt="Loading..." objectFit="cover" />
            ) : (
              isVideo(uploadedVideosURL[0]) && (
                <video
                  src={uploadedVideosURL[0]}
                  style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                  preload="metadata"
                  onClick={openViewer}
                />
              )
            )}
            {!isUploadLoading && (
              <RemoveButton
                aria-label="Remove File"
                onClick={handleRemoveMedia}
                pos="absolute"
                minW="unset"
                w="16px"
                h="16px"
                right={isRtl ? 'unset' : '4px'}
                left={isRtl ? '4px' : 'unset'}
              />
            )}
          </Box>
        )}
      </Flex>

      <HiddenFileInput accept="video/*" onChange={handleVideoUpload} ref={imageInputRef} />

      <Portal container={document.getElementById('root')}>
        <FsLightbox
          key={JSON.stringify(media)}
          toggler={isOpenLightBox}
          sources={media}
          sourceIndex={0}
          types={['video']}
        />
      </Portal>
    </DropzoneWrapper>
  )
}
